import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Session } from 'types/listings';
import InfoIcon from 'assets/bx-info-circle';
import { DATE_FORMAT } from 'utils';
import { ResourceGroupCard } from 'components/Resource/ResourceGroup/ResourceGroupCard';
import { useIsMobile } from 'WindowDimensionProvider';
import {
  CollectionHero,
  CollectionTitle,
} from 'features/Reserve/components/CollectionHero';
import MobileMappedCollectionPage from 'features/Reserve/components/MobileMappedCollectionPage';
import useResourceBookingSession from 'features/Reserve/hooks/useResourceBookingSession';
import useSessions from 'api/session/useSessions';
import { getDatesFromSessions } from 'utils/session';
import getStartOfMonth from 'utils/date/getStartOfMonth';
import getEndOfMonth from 'utils/date/getEndOfMonth';
import fromDateString from 'utils/date/fromDateString';
import { MAX_SESSIONS_PER_DAY_FOR_AVAILABILITY } from 'api/session/constants';
import { getSessionTimeOptions } from 'features/Reserve/hooks/useSessionTimeOptions';
import { selectBookableRows } from 'features/Reserve/utils/groupAvailability';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { CollectionPageNonMappedGroupsSkeleton } from 'features/Reserve/pages/Skeleton';
import {
  Wrapper,
  NoSessionsMessage,
} from 'features/Reserve/components/ResourceGroupLayout';
import { DayPickerPopover } from 'components/molecules/DayPickerPopover';
import { DateInput } from 'components/atoms/DateInput';
import { ResourceGroupV3, SessionStatusFilter } from '@kouto/types';
import { ListingSeo } from 'components/organisms/ListingSeo';

export const MappedCollectionPage = () => {
  const isMobile = useIsMobile();
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();
  const {
    listing,
    isLoadingListing,
    date: hookDate,
    isInitialLoading,
    latest,
    setResourceBookingSessionParams,
  } = useResourceBookingSession();

  const [localSelectedDate, setLocalSelectedDate] = useState<string | null>(
    null,
  );

  const effectiveDate = localSelectedDate ?? hookDate;

  const [dateForCalendarAvailability, setDateForCalendarAvailability] =
    useState<moment.Moment>(effectiveDate ? moment(effectiveDate) : moment());
  const [showDatePicker, setShowDatePicker] = useState(false);

  const pendingFocusRef = useRef(false);
  const focusDateInputOnRemount = useCallback((el: HTMLDivElement | null) => {
    if (el && pendingFocusRef.current) {
      el.focus();
      pendingFocusRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (effectiveDate) {
      setDateForCalendarAvailability(moment(effectiveDate));
    }
  }, [effectiveDate]);

  const isMapped = listing?.isMapped ?? false;

  const {
    data: monthlyData,
    sessions: monthlySessions,
    isLoading: isLoadingSessions,
  } = useSessions({
    listingId: listing?.resourceGroupCollectionId,
    from: getStartOfMonth(dateForCalendarAvailability.toDate()),
    to: getEndOfMonth(dateForCalendarAvailability.toDate()),
    skip: !listing?.resourceGroupCollectionId,
    maxSessionsPerDay: MAX_SESSIONS_PER_DAY_FOR_AVAILABILITY,
    // Server-side availability: without it, a day whose earliest sessions are all
    // booked/blocked fills the cap with dead rows and reads as fully unavailable.
    status: SessionStatusFilter.AVAILABLE,
  });

  // The capped monthly payload only tells us which days have availability; the selected day's
  // full session list (times/prices) comes from a separate uncapped single-day request.
  const {
    data: dayData,
    sessions: daySessions,
    isLoading: isLoadingDaySessions,
  } = useSessions({
    listingId: listing?.resourceGroupCollectionId,
    from: effectiveDate ? fromDateString(effectiveDate) : undefined,
    to: effectiveDate ? fromDateString(effectiveDate) : undefined,
    skip: !listing?.resourceGroupCollectionId || !effectiveDate,
  });

  // The skeleton stands in only until the first rows arrive; a later date or month keeps the cards on
  // screen, with the previous rows, while the next arrive.
  const isInitialLoadingSessions =
    (isLoadingSessions && !monthlyData) || (isLoadingDaySessions && !dayData);

  const availableDates = useMemo(
    () => getDatesFromSessions(monthlySessions),
    [monthlySessions],
  );

  const sessionsForSelectedDate = useMemo(() => {
    if (!effectiveDate) return [];
    return daySessions.filter((session) =>
      session.startDateTime.startsWith(effectiveDate),
    );
  }, [daySessions, effectiveDate]);

  const handleDateChange = useCallback(
    (newDate: string) => {
      setLocalSelectedDate(newDate);
      setResourceBookingSessionParams({ date: newDate });
      setShowDatePicker(false);
      pendingFocusRef.current = true;
    },
    [setResourceBookingSessionParams],
  );

  const handleGroupSelect = useCallback(
    (groupId: string) => {
      setResourceBookingSessionParams({
        groupId,
        date: effectiveDate,
      });
    },
    [setResourceBookingSessionParams, effectiveDate],
  );

  // Only the rows a guest could book: a continuously booked group's day comes back as open spans, and a
  // span too short for its shortest booking must not make the group read as available.
  const sessionsByGroup: Record<string, Session[]> = Object.fromEntries(
    listing?.resourceGroups?.map((group) => [
      group.id,
      selectBookableRows(filterSessionsByGroup(sessionsForSelectedDate, group)),
    ]) || [],
  );

  const thereAreSomeAvailableSessions = Object.values(sessionsByGroup).some(
    (sessions) => sessions.length > 0,
  );

  const isDateBlocked = (date: moment.Moment) => {
    return !availableDates.includes(date.format(DATE_FORMAT));
  };

  const onSelectDate = (date: Date) => {
    if (isLoadingSessions) return;
    handleDateChange(moment(date).format(DATE_FORMAT));
  };

  if (!listing) {
    return <h1>{translate('noCollectionFound')}</h1>;
  }

  if (isMapped && isMobile) {
    return (
      <>
        <ListingSeo listing={listing} />
        <MobileMappedCollectionPage
          listing={listing}
          sessionsByGroup={sessionsByGroup}
          onReserve={(groupId) => {
            setResourceBookingSessionParams({
              groupId,
            });
          }}
        />
      </>
    );
  }

  return (
    <CollectionDetailsContainer gap="24px">
      <ListingSeo listing={listing} />
      {isMapped ? (
        <CollectionTitle
          as="h1"
          allowMultipleLines
          className="collection-mapped-title"
        >
          {listing.title}
        </CollectionTitle>
      ) : (
        <CollectionHero isLoading={isLoadingListing} listing={listing} />
      )}
      <CollectionDetailsContainer>
        {isInitialLoading || isInitialLoadingSessions ? (
          <CollectionPageNonMappedGroupsSkeleton />
        ) : (
          <>
            {isMobile && isMapped ? null : (
              <>
                <DayPickerPopover
                  open={showDatePicker}
                  onOpenChange={setShowDatePicker}
                  mode="single"
                  required
                  asChild
                  isLoading={isLoadingSessions}
                  defaultMonth={dateForCalendarAvailability?.toDate()}
                  onSelect={onSelectDate}
                  footer={null}
                  selected={
                    !isDateBlocked(moment(effectiveDate))
                      ? moment(effectiveDate).toDate()
                      : undefined
                  }
                  onNextClick={(date) =>
                    setDateForCalendarAvailability(moment(date))
                  }
                  onPrevClick={(date) =>
                    setDateForCalendarAvailability(moment(date))
                  }
                  modifiers={{
                    disabled: (date) =>
                      isDateBlocked(moment(date)) || isLoadingSessions,
                  }}
                  trigger={
                    <DateInput
                      ref={focusDateInputOnRemount}
                      date={
                        effectiveDate ? moment(effectiveDate).toDate() : null
                      }
                      dateFormat="MMMM d"
                      placeholder={translate('selectDate')}
                    />
                  }
                  sideOffset={10}
                />
                {!thereAreSomeAvailableSessions && !isMapped && (
                  <Wrapper marginBottom={16}>
                    <NoSessionsMessage isMobile={isMobile}>
                      <InfoIcon />
                      <span>{translate('noAvailableSessionsOnThisDate')}</span>
                    </NoSessionsMessage>
                  </Wrapper>
                )}
              </>
            )}
            {[...(listing?.resourceGroups ?? [])]
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((group) => {
                const groupSessions = sessionsByGroup[group.id] || [];
                const allSessionTimes = getSessionTimeOptions(
                  groupSessions,
                  locale,
                );
                return (
                  <ResourceGroupCard
                    allSessionTimes={allSessionTimes}
                    categoryName={listing.category?.name}
                    collectionId={listing.resourceGroupCollectionId}
                    collectionName={listing.title ?? 'Title unavailable'}
                    group={group}
                    handleGroupSelect={handleGroupSelect}
                    key={group.id}
                    latest={latest}
                    scheduleType={listing.scheduleType}
                    sessions={groupSessions}
                    showTruncatedView={isMapped}
                  />
                );
              })}
          </>
        )}
      </CollectionDetailsContainer>
    </CollectionDetailsContainer>
  );
};

const CollectionDetailsContainer = styled.div<{ gap?: CSSProperties['gap'] }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.gap || '24px'};
`;

const filterSessionsByGroup = (
  sessions: Session[],
  resourceGroup: ResourceGroupV3,
) => {
  return sessions.filter(
    (session) =>
      !!resourceGroup.experiences.find(
        (exp) => exp.id === session.experienceId,
      ),
  );
};
